import React, { useMemo, useState } from "react";
import { Box, Button, Popover, Stack, useTheme } from "@mui/material";
import { format } from "date-fns";
import { DateRange } from "react-date-range";
import type {
  DateRangeProps,
  Range,
  RangeKeyDict,
} from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { useDateRangeThemeSx } from "../hooks/useDateRangeThemeSx";
import {
  formatDayRangeLabel,
  formatDayRangeValue,
  parseDayRangeValue,
  reviewDateRangeLocale,
} from "../utils/reviewDateFilterOptions";

const DateRangePicker = DateRange as React.ComponentType<DateRangeProps>;

function parseYmdToLocalDate(ymd: string): Date {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y!, m! - 1, d);
}

interface ReviewDayRangePickerProps {
  value: string;
  onChange: (value: string) => void;
}

const ReviewDayRangePicker: React.FC<ReviewDayRangePickerProps> = ({
  value,
  onChange,
}) => {
  const theme = useTheme();
  const dateRangeThemeSx = useDateRangeThemeSx();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const parsed = parseDayRangeValue(value);

  const [rangeDraft, setRangeDraft] = useState<Range[]>(() => [
    {
      startDate: parsed ? parseYmdToLocalDate(parsed.start) : new Date(),
      endDate: parsed ? parseYmdToLocalDate(parsed.end) : new Date(),
      key: "selection",
    },
  ]);

  const label = useMemo(() => formatDayRangeLabel(value), [value]);

  const openPicker = (e: React.MouseEvent<HTMLElement>) => {
    const p = parseDayRangeValue(value);
    setRangeDraft([
      {
        startDate: p ? parseYmdToLocalDate(p.start) : new Date(),
        endDate: p ? parseYmdToLocalDate(p.end) : new Date(),
        key: "selection",
      },
    ]);
    setAnchorEl(e.currentTarget);
  };

  const closePicker = () => setAnchorEl(null);

  const applyRange = () => {
    const sel = rangeDraft[0];
    if (!sel?.startDate || !sel?.endDate) return;
    const start = format(sel.startDate, "yyyy-MM-dd");
    const end = format(sel.endDate, "yyyy-MM-dd");
    const [startYmd, endYmd] = start <= end ? [start, end] : [end, start];
    onChange(formatDayRangeValue(startYmd, endYmd));
    closePicker();
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={openPicker}
        sx={{ minWidth: 200, justifyContent: "flex-start", textTransform: "none" }}
      >
        {label}
      </Button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closePicker}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              p: 2,
              maxWidth: "calc(100vw - 24px)",
              bgcolor: "background.paper",
            },
          },
        }}
      >
        <Stack spacing={2} alignItems="flex-end">
          <Box sx={dateRangeThemeSx}>
            <DateRangePicker
              ranges={rangeDraft}
              onChange={(ranges: RangeKeyDict) => {
                const sel = ranges.selection;
                if (sel) setRangeDraft([{ ...sel, key: "selection" }]);
              }}
              moveRangeOnFirstSelection={false}
              months={1}
              direction="horizontal"
              locale={reviewDateRangeLocale}
              weekStartsOn={1}
              color={theme.palette.primary.main}
              rangeColors={[theme.palette.primary.main]}
            />
          </Box>
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={closePicker}>
              Cancel
            </Button>
            <Button size="small" variant="contained" onClick={applyRange}>
              Apply
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};

export default ReviewDayRangePicker;
